import React from "react";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    FileText,
    CheckSquare,
    Image as ImageIcon,
    MessageSquare,
    MoreHorizontal,
    ThumbsUp,
    MessageCircle,
    Eye,
    Plus
} from "lucide-react";

const WorkspacePage = () => {
    const createItems = [
        { icon: FileText, label: "Document", description: "Write stuff", color: "text-shiftleft-500", bgColor: "bg-shiftleft-50" },
        { icon: CheckSquare, label: "Task", description: "Track work", color: "text-green-500", bgColor: "bg-green-50" },
        { icon: ImageIcon, label: "Post", description: "Share ideas", color: "text-orange-500", bgColor: "bg-orange-50" },
        { icon: MessageSquare, label: "Message", description: "Start a chat", color: "text-purple-500", bgColor: "bg-purple-50" },
    ];

    const activityFeed = [
        {
            id: 1,
            user: { name: "Mason Clay", dept: "Engineering", avatar: "/avatars/mason.png" },
            time: "May 21st at 11:02am",
            content: (
                <div className="space-y-4 shadow-sm border border-[#f1f1ef] rounded-lg bg-white p-5 mt-4">
                    <p className="text-[15px] leading-relaxed">
                        <span className="text-shiftleft-600 font-medium">@ChatGPT</span> please write an executive summary of everything the engineering team worked in Q1 2026 and whether we met our estimates from <span className="underline decoration-gray-300">📄 Roadmap Q1 2026</span>.
                    </p>
                    <p className="text-[15px] leading-relaxed">
                        cc <span className="text-shiftleft-600 font-medium">@Cass</span> let's use this for our retro today
                    </p>
                    <div className="flex items-center gap-4 pt-4 border-t border-[#f1f1ef]">
                        <div className="flex items-center gap-1 text-gray-400 text-sm">
                            <ThumbsUp className="w-4 h-4" />
                            <span>2</span>
                        </div>
                        <div className="flex -space-x-2 ml-auto">
                            {[1, 2].map(i => (
                                <Avatar key={i} className="w-6 h-6 border-2 border-white">
                                    <AvatarFallback className="text-[8px]">U{i}</AvatarFallback>
                                </Avatar>
                            ))}
                            <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-[8px] text-gray-500">+3</div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 2,
            user: { name: "Rose Compás", action: "shared a task collection", avatar: "/avatars/rose.png" },
            time: "May 21st at 10:32am",
            content: (
                <div className="shadow-sm border border-[#f1f1ef] rounded-lg bg-white overflow-hidden mt-4">
                    <div className="p-4 border-b border-[#f1f1ef] bg-[#fafafa]">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="font-semibold text-sm">Recruiting</span>
                        </div>
                    </div>
                    <div className="divide-y divide-[#f1f1ef]">
                        {[
                            { label: "Interview Cara Bina", status: "in-progress" },
                            { label: "Add listing to CSM industry job board", status: "todo" },
                            { label: "LinkedIn candidate sourcing", status: "todo" },
                        ].map((task, i) => (
                            <div key={i} className="p-4 flex items-center gap-3 hover:bg-[#f7f7f5] transition-colors cursor-pointer">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${task.status === 'in-progress' ? 'border-shiftleft-500' : 'border-gray-200'}`}>
                                    {task.status === 'in-progress' && <div className="w-2.5 h-2.5 bg-shiftleft-500 rounded-full"></div>}
                                </div>
                                <span className="text-[14px] text-gray-700">{task.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )
        }
    ];

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-12 pb-20">
            {/* Create Section */}
            <section>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-[17px] font-bold">Create</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {createItems.map((item) => (
                        <Card key={item.label} className="group hover:bg-[#f7f7f5] transition-all duration-200 cursor-pointer border-[#ececeb] shadow-none">
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl ${item.bgColor} flex items-center justify-center transition-transform group-hover:scale-105`}>
                                    <item.icon className={`w-6 h-6 ${item.color}`} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="font-semibold text-[15px]">{item.label}</span>
                                    <span className="text-[12px] text-gray-500">{item.description}</span>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </section>

            {/* For You Section */}
            <section>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-[17px] font-bold">For you</h2>
                </div>
                <div className="space-y-8">
                    {activityFeed.map((item) => (
                        <div key={item.id} className="group relative">
                            <div className="flex items-start gap-3">
                                <Avatar className="w-10 h-10 ring-2 ring-white">
                                    <AvatarFallback className="bg-shiftleft-100 text-shiftleft-600 font-medium">
                                        {item.user.name.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-bold text-[15px]">{item.user.name}</span>
                                        {item.user.dept && <span className="text-gray-400 text-[14px]">in {item.user.dept}</span>}
                                        {item.user.action && <span className="text-gray-500 text-[14px]">{item.user.action}</span>}
                                    </div>
                                    <div className="text-[12px] text-gray-400 mb-2">{item.time}</div>
                                    {item.content}
                                </div>
                                <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 h-8 w-8 text-gray-400">
                                    <MoreHorizontal className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Floating Action Button - Notch style */}
            <Button
                className="fixed bottom-8 right-8 w-14 h-14 rounded-full shadow-2xl bg-shiftleft-600 hover:bg-shiftleft-700 text-white flex items-center justify-center p-0 transition-transform hover:scale-110 active:scale-95"
            >
                <Plus className="w-7 h-7" />
            </Button>
        </div>
    );
};

export default WorkspacePage;
